import React, { useEffect, useState } from 'react';
import { useAccount, useContractRead } from 'wagmi';
import { createPublicClient, http, formatEther } from 'viem';
import { pulsechainV4 } from 'wagmi/chains';
import contractRaffle from '../contractData/contractRaffle.json';
import { Link } from 'react-router-dom';
import defaultImage from '../assets/logo.webp';
import '../Pages/ownerReports.css';
import { useLocation, useNavigate } from 'react-router-dom';

const address = contractRaffle.address;
const ABI = contractRaffle.abi;

const publicClient = createPublicClient({
  chain: pulsechainV4,
  transport: http()
});

const OwnerAllReportedRaffles = () => {
  const { address: walletAddress, isConnected } = useAccount();
  const [allReports, setAllReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [expandedReport, setExpandedReport] = useState(null);
  const { state } = useLocation();
  const navigate = useNavigate();

  // Check if current user is owner
  const { data: contractOwner, isLoading: isOwnerLoading } = useContractRead({
    address,
    abi: ABI,
    functionName: 'owner',
    enabled: isConnected // Only fetch if wallet is connected
  });

  useEffect(() => {
    if (walletAddress && contractOwner) {
      setIsOwner(walletAddress.toLowerCase() === contractOwner.toLowerCase());
    } else {
      setIsOwner(false);
    }
  }, [walletAddress, contractOwner]);


  useEffect(() => {
  const fetchAllReports = async () => {
    if (!isOwner || !walletAddress) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Get all reported raffles
      const reports = await publicClient.readContract({
        address,
        abi: ABI,
        functionName: 'getAllReportedRaffles',
        account: walletAddress
      });

      // Transform the data with proper error handling
      const formattedReports = await Promise.all(
        reports.map(async (report) => {
          try {
            const raffle = await publicClient.readContract({
              address,
              abi: ABI,
              functionName: 'raffles',
              args: [report.raffleId]
            });

            const imageUrl = await publicClient.readContract({
              address,
              abi: ABI,
              functionName: 'image',
              args: [report.raffleId]
            });

            // Safely format the ticket price
            const ticketPrice = raffle.ticketPrice 
              ? formatEther(raffle.ticketPrice.toString())
              : '0';

            return {
              reportIndex: Number(report.reportIndex),
              raffleId: Number(report.raffleId),
              creator: report.creator || '0x0000000000000000000000000000000000000000',
              startTime: Number(report.startTime) || 0,
              endTime: Number(report.endTime) || 0,
              reporter: report.reporter || '0x0000000000000000000000000000000000000000',
              timestamp: Number(report.timestamp) || 0,
              raffleName: raffle.name || 'Unnamed Raffle',
              raffleImage: imageUrl || defaultImage,
              ticketPrice: ticketPrice
            };
          } catch (err) {
            console.error(`Error processing report ${report.reportIndex}:`, err);
            return null; // Return null for failed reports
          }
        })
      );

      // Filter out any null values from failed reports
      const validReports = formattedReports.filter(report => report !== null);
      
      setAllReports(validReports);
    } catch (err) {
      console.error("Error fetching reports:", err);
      setError("Failed to load reports. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  fetchAllReports();
}, [isOwner, walletAddress, publicClient]);
  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  const toggleReportDetails = (reportIndex) => {
    setExpandedReport(expandedReport === reportIndex ? null : reportIndex);
  };

  if (!walletAddress) {
    return (
      <div className="connect-wallet-message">
        <h2>All Reported sdsd</h2>
        <p>Please connect your wallet to access this page.</p>
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="unauthorized-container">
        <h2>Access Denied</h2>
        <p>This page is only accessible to the contract owner.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading all reports...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>All Reported Raffles</h2>
        <p className="error-message">{error}</p>
        <button onClick={() => window.location.reload()} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (allReports.length === 0) {
    return (
      <div className="no-raffles-container">
        <h2>All Reported Raffles</h2>
        <p>No raffles have been reported yet.</p>
      </div>
    );
  }

  return (
    <div className="owner-all-reported-container">
      <div className="header-section">
        <h2>All Reported Raffles</h2>
        <p className="stats-summary">
          Showing {allReports.length} reports
        </p>
      </div>

      <div className="reports-table">
        <div className="table-header">
          <div className="header-cell">Report #</div>
          <div className="header-cell">Raffle</div>
          <div className="header-cell">Creator</div>
          <div className="header-cell">Reporter</div>
          <div className="header-cell">Date</div>
        </div>

        {allReports.map((report) => (
          <div key={report.reportIndex} className="report-row">
            <div className="table-cell">{report.reportIndex}</div>
            <div className="table-cell">
  <div className="raffle-info">
    <img 
      src={report.raffleImage || defaultImage} 
      alt={report.raffleName}
      className="raffle-thumbnail"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = defaultImage;
      }}
    />
    <div>
      <div onClick={() => navigate(`/raffle/${report.raffleId}`, { state: { report } })}>
        ID: {report.raffleId}
      </div>
    </div>
  </div>
</div>
            <div className="table-cell">
              <a 
                href={`https://scan.v4.testnet.pulsechain.com/#/address/${report.creator}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {formatAddress(report.creator)}
              </a>
            </div>
            <div className="table-cell">
              <a 
                href={`https://scan.v4.testnet.pulsechain.com/#/address/${report.reporter}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {formatAddress(report.reporter)}
              </a>
            </div>
            <div className="table-cell">{formatDate(report.timestamp)}</div>
           {/*} <div className="table-cell">
              <button 
                className="toggle-details-button"
                onClick={() => toggleReportDetails(report.reportIndex)}
              >
                {expandedReport === report.reportIndex ? 'Hide' : 'View'}
              </button>
            </div>*/}

            {expandedReport === report.reportIndex && (
              <div className="report-details">
                <div className="detail-row">
                  <span className="detail-label">Start Date:</span>
                  <span>{formatDate(report.startTime)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">End Date:</span>
                  <span>{formatDate(report.endTime)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Ticket Price:</span>
                  <span>{report.ticketPrice} PLS</span>
                </div>
                <div className="action-buttons">
                  <Link 
                    to={`/raffle/${report.raffleId}`} 
                    className="view-button"
                  >
                    View Raffle
                  </Link>
                  <button className="disable-button">
                    Disable Raffle
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OwnerAllReportedRaffles;